import { useState } from "react";
import { CurrencySwitchDialog } from "./CurrencySwitchDialog";
import { ChangeMarketAction } from "./ChangeMarketAction";
import type { CartCurrencyStatus } from "@/types/currencyMigration";
import { fillTemplate } from "@/utils/text";
import { t, useLocalizedTexts } from "@/utils/translations";
import styles from "./CurrencySwitchDialog.module.css";

interface CartCurrencyWarningProps {
  status: CartCurrencyStatus | null;
  /** Re-check the status after a switch, so the warning clears itself. */
  onSwitched: () => void;
}

/**
 * Warns on the CART that the member's billing currency is not the cart's, and offers
 * the switch there and then.
 *
 * The conflict is knowable the moment a pinned member looks at a cart in another
 * currency, and leaving it to the payment step meant they filled in an address and
 * chose a delivery method before finding out they could not pay at all. This is the
 * same switch as the checkout's, moved to the first screen that can show it.
 *
 * Renders nothing unless there is a genuine, known conflict — the status endpoint
 * answers "no conflict" for guests, unpinned members and every kind of unknown.
 */
export function CartCurrencyWarning({
  status,
  onSwitched,
}: CartCurrencyWarningProps) {
  const texts = useLocalizedTexts();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!status?.conflict || !status.cartCurrency) {
    return null;
  }

  const cartCurrency = status.cartCurrency;
  const cart = cartCurrency.toUpperCase();
  const billing = status.billingCurrency?.toUpperCase() ?? "";
  const country = status.customerCountry?.toUpperCase() ?? "";

  // The shop does not serve this market at all. Neither their billing nor their
  // address is wrong, and the currency switch cannot help — say so, and point at the
  // one thing that does: their country's own store.
  if (status.conflict === "currency_not_sold_to_country") {
    return (
      <div className={styles.warning} role="status">
        <div className={styles.warningText}>
          <h3 className={styles.noticeTitle}>
            {fillTemplate(
              t(
                texts,
                "Tracker.Cart.CurrencyMismatch.MarketTitle",
                "We don't sell in {cartCurrency} to {country}"
              ),
              { cartCurrency: cart, country }
            )}
          </h3>
          <p className={styles.noticeBody}>
            {fillTemplate(
              t(
                texts,
                "Tracker.Cart.CurrencyMismatch.MarketBody",
                "This shop prices in {cartCurrency}, which we can't sell to {country}. Switch to your own country's shop to buy this — that starts a new basket there."
              ),
              { cartCurrency: cart, country }
            )}
          </p>
        </div>

        <ChangeMarketAction
          billingCurrency={status.billingCurrency}
          isPrimary
        />
      </div>
    );
  }

  // The pinned branch names the member's own currency; without it there is nothing
  // truthful to say, so stay silent.
  const billingCurrency = status.billingCurrency;
  if (!billingCurrency) {
    return null;
  }

  const handleSwitched = () => {
    setIsDialogOpen(false);
    onSwitched();
  };

  return (
    <>
      <div className={styles.warning} role="status">
        <div className={styles.warningText}>
          <h3 className={styles.noticeTitle}>
            {fillTemplate(
              t(
                texts,
                "Tracker.Cart.CurrencyMismatch.Title",
                "Your account is billed in {currency}"
              ),
              { currency: billing }
            )}
          </h3>
          <p className={styles.noticeBody}>
            {status.canSwitch
              ? fillTemplate(
                  t(
                    texts,
                    "Tracker.Cart.CurrencyMismatch.Body",
                    "This cart is priced in {cartCurrency}, and a payment can only be taken in one currency. Switch your billing to {cartCurrency} to check out — nothing is charged for switching."
                  ),
                  { currency: billing, cartCurrency: cart }
                )
              : fillTemplate(
                  t(
                    texts,
                    "Tracker.Cart.CurrencyMismatch.BodyNoSwitch",
                    "This cart is priced in {cartCurrency}, and a payment can only be taken in one currency. You can shop in {currency} instead — that starts a new basket — or contact support."
                  ),
                  { currency: billing, cartCurrency: cart }
                )}
          </p>
        </div>

        {!status.canSwitch && (
          <ChangeMarketAction
            billingCurrency={status.billingCurrency}
            isPrimary
          />
        )}

        {status.canSwitch && (
          <button
            type="button"
            className={`${styles.button} ${styles.primary}`}
            onClick={() => setIsDialogOpen(true)}
          >
            {fillTemplate(
              t(
                texts,
                "Tracker.Checkout.CurrencySwitch.NoticeAction",
                "Switch to {targetCurrency}"
              ),
              { targetCurrency: cart }
            )}
          </button>
        )}
      </div>

      {isDialogOpen && (
        <CurrencySwitchDialog
          existingCurrency={billingCurrency}
          requestedCurrency={cartCurrency}
          onSwitched={handleSwitched}
          onDismiss={() => setIsDialogOpen(false)}
        />
      )}
    </>
  );
}
